// Declarative hash-route table for App.tsx (issue #416, decision ratified on
// the issue 2026-08-22: "hand-rolled declarative route table, no router lib").
//
// `resolveRoute(hash)` is the single source of truth for "given the current
// hash, what should the app's route-derived state become". It replaces the
// order-dependent if/else chain that used to live inline in App.tsx's
// `checkHash` effect, so the mapping is unit-testable without rendering the
// component tree.
//
// A `RoutePatch` only lists the fields a given route actually changes —
// omitted keys mean "leave the current value alone", matching the original
// code's behavior of only touching the state variables each branch actually
// assigned. This isn't a full route-state snapshot; call sites apply the
// patch on top of whatever state already exists.

export type PageKey =
  | 'scanner'
  | 'reports'
  | 'queue'
  | 'billing'
  | 'monitoring'
  | 'admin-policy-analyzer'
  | 'admin-users'
  | 'admin-user-detail';

/** The pages the dashboard header can navigate to. */
export type NavPage = 'scanner' | 'reports' | 'queue' | 'billing' | 'monitoring';

/** A one-off credit purchase (Payment Link) or a recurring-plan checkout (#1315). */
/** 'unlock' is a signed-out unlock checkout's return (2026-10-27); its `report` param is the scan id. */
export type PaymentMode = 'credits' | 'subscription' | 'unlock';

export interface RoutePatch {
  currentPage?: PageKey;
  scanRouteId?: string | null;
  reportRouteId?: string | null;
  salesReportId?: string | null;
  salesReportToken?: string | null;
  paymentSessionId?: string | null;
  /** What the #/payment-success session bought (#1315); only that route sets it. */
  paymentMode?: PaymentMode;
  /**
   * The report a monitoring checkout was started from (#1776), read off
   * #/payment-success's `report` param. Only a well-formed id survives.
   */
  paymentReportId?: string | null;
  /**
   * The unlock Checkout Session a #/scan/:id return carries (#1775/#1776):
   * the report waits on the webhook's unlock while it is set.
   */
  unlockSessionId?: string | null;
  policyAnalyzerId?: string | null;
  adminUserId?: string | null;
  showPaymentBanner?: boolean;
  // Mirrors the original code's habit of nulling currentResult + resetting
  // the batch-result state together whenever a route isn't scan/report-ish.
  clearResult?: boolean;
}

export interface RouteResolution {
  patch: RoutePatch;
  // '#/terms' and '#/privacy' don't render anything in-app — they open the
  // marketing site in a new tab and bounce the hash back to '#/'.
  externalRedirect?: 'terms' | 'privacy';
}

/** A scan or report id safe to put back into a route: no separators, no query. */
const ROUTE_ID = /^[A-Za-z0-9_-]{1,128}$/;

export function validRouteId(raw: string | null): string | null {
  return raw && ROUTE_ID.test(raw) ? raw : null;
}

/** One parser for both a real query string and the query portion of a hash. */
export function parseParams(queryLike: string): URLSearchParams {
  return new URLSearchParams(queryLike);
}

/** Extracts and parses the `?...` portion embedded inside a hash route. */
export function parseHashQuery(hash: string): URLSearchParams {
  const qIndex = hash.indexOf('?');
  return parseParams(qIndex === -1 ? '' : hash.slice(qIndex + 1));
}

// Every branch below other than "sales" resets salesReportId/salesReportToken
// (the original code did this unconditionally once the sales pattern failed
// to match, before evaluating any further branch).
const RESET_SALES: RoutePatch = { salesReportId: null, salesReportToken: null };
// Every branch below other than "policy-analyzer" additionally resets
// policyAnalyzerId/adminUserId (once past the sales *and* admin-page checks).
const RESET_POLICY_AND_ADMIN: RoutePatch = { ...RESET_SALES, policyAnalyzerId: null, adminUserId: null };
// Every branch below other than "payment-success" additionally resets
// paymentSessionId.
function paymentModeOf(mode: string | null): PaymentMode {
  if (mode === 'subscription' || mode === 'unlock') return mode;
  return 'credits';
}

const RESET_PAYMENT: RoutePatch = { ...RESET_POLICY_AND_ADMIN, paymentSessionId: null, paymentReportId: null, unlockSessionId: null };
// Every branch below other than "scan"/"report" additionally resets
// scanRouteId/reportRouteId.
const RESET_SCAN_AND_REPORT: RoutePatch = { ...RESET_PAYMENT, scanRouteId: null, reportRouteId: null };

interface RouteRule {
  pattern: RegExp;
  toResolution: (match: RegExpMatchArray, hash: string) => RouteResolution;
}

const ROUTES: RouteRule[] = [
  {
    pattern: /^#\/sales\/([^?]+)(?:\?token=(.+))?$/,
    toResolution: (match) => ({
      patch: {
        salesReportId: decodeURIComponent(match[1]),
        salesReportToken: match[2] ? decodeURIComponent(match[2]) : null,
        scanRouteId: null,
        reportRouteId: null,
        paymentSessionId: null,
      },
    }),
  },
  {
    pattern: /^#\/admin\/policy-analyzer(?:\/(.+))?$/,
    toResolution: () => ({
      patch: {
        ...RESET_POLICY_AND_ADMIN,
        currentPage: 'admin-policy-analyzer',
        scanRouteId: null,
        reportRouteId: null,
        paymentSessionId: null,
        clearResult: true,
      },
    }),
  },
  {
    pattern: /^#\/admin\/users\/(.+)$/,
    toResolution: (match) => ({
      patch: {
        ...RESET_SALES,
        currentPage: 'admin-user-detail',
        adminUserId: decodeURIComponent(match[1]),
        policyAnalyzerId: null,
        scanRouteId: null,
        reportRouteId: null,
        paymentSessionId: null,
        clearResult: true,
      },
    }),
  },
  {
    pattern: /^#\/admin\/users$/,
    toResolution: () => ({
      patch: {
        ...RESET_POLICY_AND_ADMIN,
        currentPage: 'admin-users',
        scanRouteId: null,
        reportRouteId: null,
        paymentSessionId: null,
        clearResult: true,
      },
    }),
  },
  {
    pattern: /^#\/policy-analyzer\/(.+)$/,
    toResolution: (match) => ({
      patch: {
        ...RESET_SALES,
        policyAnalyzerId: decodeURIComponent(match[1]),
        scanRouteId: null,
        reportRouteId: null,
        paymentSessionId: null,
      },
    }),
  },
  {
    pattern: /^#\/payment-success/,
    toResolution: (_match, hash) => {
      const query = parseHashQuery(hash);
      return {
        patch: {
          ...RESET_POLICY_AND_ADMIN,
          paymentSessionId: query.get('session_id'),
          // Only the billing checkout's success_url carries mode=subscription,
          // and only the signed-out unlock checkout's carries mode=unlock; the
          // Payment Link's carries no mode, so anything else is credits.
          paymentMode: paymentModeOf(query.get('mode')),
          paymentReportId: validRouteId(query.get('report')),
          unlockSessionId: null,
          scanRouteId: null,
          reportRouteId: null,
        },
      };
    },
  },
  {
    pattern: /^#\/scan\/(.+)$/,
    toResolution: (match, hash) => {
      const rawScanId = match[1];
      return {
        patch: {
          ...RESET_PAYMENT,
          scanRouteId: decodeURIComponent(rawScanId.split('?')[0]),
          reportRouteId: null,
          currentPage: 'scanner',
          showPaymentBanner: rawScanId.includes('payment=success'),
          unlockSessionId: parseHashQuery(hash).get('session_id') || null,
        },
      };
    },
  },
  {
    pattern: /^#\/report\/(.+)$/,
    toResolution: (match) => ({
      patch: {
        ...RESET_PAYMENT,
        reportRouteId: decodeURIComponent(match[1]),
        scanRouteId: null,
        currentPage: 'scanner',
      },
    }),
  },
];

const EXACT_ROUTES: Record<string, RouteResolution> = {
  '#/reports': { patch: { ...RESET_SCAN_AND_REPORT, currentPage: 'reports', clearResult: true } },
  '#/queue': { patch: { ...RESET_SCAN_AND_REPORT, currentPage: 'queue', clearResult: true } },
  '#/terms': { patch: { ...RESET_SCAN_AND_REPORT }, externalRedirect: 'terms' },
  '#/privacy': { patch: { ...RESET_SCAN_AND_REPORT }, externalRedirect: 'privacy' },
  '#/billing': { patch: { ...RESET_SCAN_AND_REPORT, currentPage: 'billing', clearResult: true } },
  '#/monitoring': { patch: { ...RESET_SCAN_AND_REPORT, currentPage: 'monitoring', clearResult: true } },
};

const DEFAULT_RESOLUTION: RouteResolution = {
  patch: { ...RESET_SCAN_AND_REPORT, currentPage: 'scanner' },
};

/**
 * Resolves a `window.location.hash` string into the state patch App.tsx's
 * routing effect should apply, plus any external-redirect side effect.
 *
 * Order matters: patterns are tried in the same precedence the original
 * if/else chain used (sales → admin pages → policy-analyzer →
 * payment-success → scan → report → exact-string routes → default).
 *
 * Note: `hash.startsWith('#/payment-success')` is handled entirely by the
 * `payment-success` pattern above, which always returns before the exact
 * string table is consulted — the original code's own final "startsWith
 * payment-success" branch in that table was unreachable for the same reason
 * and is intentionally not reproduced here.
 */
export function resolveRoute(hash: string): RouteResolution {
  for (const route of ROUTES) {
    const match = hash.match(route.pattern);
    if (match) return route.toResolution(match, hash);
  }
  return EXACT_ROUTES[hash] ?? DEFAULT_RESOLUTION;
}
